import { useCallback, useMemo } from 'react';
import { NO_DURATION_DEFINED } from '../../../../helpers/axiosWrapper/cache';
import useLocations, { Airport } from './useLocations';
import { useState } from 'react';

interface UseOriginDestinationProps {
  dispatch: any;
  origin: Airport;
  destination: Airport;
  filter: any;
  initialOrigins?: Airport[];
  initialDestinations?: Airport[];
  originDestinationLogic: string;
  cache?: boolean;
  cacheDuration?: number;
  travelerInterest?: $TSFixMe;
  showInitialData?: boolean;
  suppressLocationTracking?: boolean;
}

const useOriginDestination = ({
  dispatch,
  origin,
  destination,
  filter: { getAirports, meta, pageContext, integrationSettings, prePopulation },
  initialOrigins,
  initialDestinations,
  cache = false,
  cacheDuration = NO_DURATION_DEFINED,
  travelerInterest = {},
  showInitialData = false,
  suppressLocationTracking = false
}: UseOriginDestinationProps) => {
  const [originSelected, setOriginSelected] = useState(origin.value);
  const [destinationSelected, setDestinationSelected] = useState(destination.value);

  const origins = useLocations({
    getAirports,
    meta,
    pageContext,
    integrationSettings,
    prePopulation,
    initialData: initialOrigins,
    airportType: 'ORIGIN',
    cache,
    cacheDuration
  });

  const destinations = useLocations({
    getAirports,
    meta,
    pageContext,
    integrationSettings,
    prePopulation,
    initialData: initialDestinations,
    airportType: 'DESTINATION',
    cache,
    cacheDuration,
    selectedOrigin: origin.value,
    travelerInterest,
    showInitialData
  });

  const clearOrigin = useCallback(() => {
    if (originSelected) {
      setOriginSelected('');
      setDestinationSelected('');
    }
    if (!suppressLocationTracking) {
      dispatch({ type: 'ORIGIN_CLEAR' });
    }
  }, [dispatch, originSelected, suppressLocationTracking]);

  const clearDestination = useCallback(() => {
    if (destinationSelected) {
      setDestinationSelected('');
    }
    if (!suppressLocationTracking) {
      dispatch({ type: 'DESTINATION_CLEAR' });
    }
  }, [destinationSelected, dispatch, suppressLocationTracking]);

  const selectOrigin = useCallback(
    (newOrigin) => {
      dispatch({ type: 'ORIGIN_CHANGE', payload: newOrigin });
    },
    [dispatch]
  );

  const selectDestination = useCallback(
    (newDestination) => {
      dispatch({ type: 'DESTINATION_CHANGE', payload: newDestination });
    },
    [dispatch]
  );

  const selectOriginState = useCallback(
    ({ value }: any) => {
      if (destinationSelected === value) {
        setDestinationSelected('');
      }
      setOriginSelected(value);
    },
    [destinationSelected]
  );

  const selectDestinationState = useCallback(({ value }: any) => {
    setDestinationSelected(value);
  }, []);

  const swapLocations = useCallback(() => {
    dispatch({ type: 'SWAP_LOCATIONS' });
  }, [dispatch]);

  return useMemo(
    () => ({
      origin: {
        ...origins,
        value: origin.value,
        valueSelect: originSelected,
        onChange: selectOrigin,
        onClear: clearOrigin,
        selectOrigin: selectOriginState
      },
      destination: {
        ...destinations,
        value: destination.value,
        valueSelect: destinationSelected,
        onChange: selectDestination,
        onClear: clearDestination,
        selectDestination: selectDestinationState
      },
      swapLocations
    }),
    [
      clearDestination,
      clearOrigin,
      destination.value,
      destinationSelected,
      destinations,
      origin.value,
      originSelected,
      origins,
      selectDestination,
      selectDestinationState,
      selectOrigin,
      selectOriginState,
      swapLocations
    ]
  );
};

export default useOriginDestination;
